import { VEvent } from 'node-ical'
import moment from 'moment-timezone'
import { RRule } from 'rrule'
import { convertToRRuleOptions } from '../utils/rruleCompat'
const CRLF = '\r\n'

// 필요한 경우 사용할 수 있는 추가 타입 정의
export interface ICSOptions {
  method?:
    | 'PUBLISH'
    | 'REQUEST'
    | 'REPLY'
    | 'ADD'
    | 'CANCEL'
    | 'REFRESH'
    | 'COUNTER'
    | 'DECLINECOUNTER'
  comment?: string
  targetExceptionRecurrenceId?: Date
}

/**
 * VEvent 객체를 ICS 형식의 문자열로 변환합니다.
 * @param event - node-ical의 VEvent 객체
 * @returns ICS 형식의 문자열
 */
export function generateICS(event: VEvent, options?: ICSOptions): string {
  let eventStrings: string[] = []
  if (event.rrule && event.recurrences) {
    Object.values(event.recurrences).forEach((recurrence) => {
      const rec = recurrence as VEvent
      let comment = null
      if (
        options?.targetExceptionRecurrenceId &&
        (rec.recurrenceid as Date)?.getTime() === options.targetExceptionRecurrenceId.getTime()
      ) {
        comment = options?.comment
      }
      const eventString = _eventToICSString(rec, event, comment)
      if (eventString) {
        eventStrings.push(eventString)
      }
    })
  }

  const eventString = _eventToICSString(
    event,
    null,
    options?.targetExceptionRecurrenceId == null ? options?.comment : null,
  )
  if (eventString) {
    eventStrings.unshift(eventString)
  }

  const startTZ = event.start.tz
  const endTZ = event.end.tz

  let tzStrings = []
  if (startTZ) {
    tzStrings.push(generateVTimezone(startTZ))
  }
  if (endTZ && startTZ !== endTZ) {
    tzStrings.push(generateVTimezone(endTZ))
  }

  let returnString = 'BEGIN:VCALENDAR' + CRLF
  returnString += 'VERSION:2.0' + CRLF
  returnString += 'PRODID:-//NextIntelligence.ai, Inc.//OfficeMail OWA//EN' + CRLF
  if (tzStrings.length > 0) {
    const tzString = tzStrings.join(CRLF)
    returnString += tzString
  }
  const eventsStr = eventStrings.join(CRLF)
  returnString += eventsStr
  returnString += CRLF + 'END:VCALENDAR' + CRLF

  return returnString
}

const encodeValue = (value) => {
  if (!value) {
    return value
  }
  return value.replace(/\\/g, '\\\\').replace(/\n/g, '\\n')
}

// RFC 5545 ORGANIZER/ATTENDEE 의 calendar user address 는 mailto: URI 형식이어야 한다.
// EventData 정규 경로는 항상 mailto: 를 prepend 하지만, 외부에서 직접 주입된 VEvent 가
// bare email 을 가질 수 있으므로 출력 시점에 한 번 더 보장한다.
const ensureMailtoPrefix = (value: string): string => {
  if (!value) return value
  return value.toLowerCase().startsWith('mailto:') ? value : `mailto:${value}`
}

function _eventToICSString(event: VEvent, parent?: VEvent, comment?: string): string {
  let appendString = ''
  if (event.exdate) {
    const dateType = parent?.datetype ?? event.datetype
    Object.values(event.exdate).forEach((exdate) => {
      if (dateType === 'date-time') {
        const momentDate = moment(exdate).tz(event.start.tz ?? 'Etc/UTC')
        const momentTzId = momentDate.zoneName()
        if (momentTzId === 'UTC' || momentTzId === 'GMT') {
          appendString += `EXDATE:${momentDate.format('YYYYMMDDTHHmmss[Z]')}${CRLF}`
        } else {
          appendString += `EXDATE;TZID=${event.start.tz}:${momentDate.format('YYYYMMDDTHHmmss')}${CRLF}`
        }
      } else {
        appendString += `EXDATE;VALUE=DATE:${moment(exdate).format('YYYYMMDD')}${CRLF}`
      }
    })
  }

  if (comment) {
    appendString += `COMMENT:${encodeValue(comment)}${CRLF}`
  }

  return _eventToICSStringWithParent(parent, event, appendString)
}

function _eventToICSStringWithParent(
  parent: VEvent | null,
  event: VEvent,
  appendString: string,
): string {
  const masterDateType = parent?.datetype ?? event.datetype
  const masterStartTZ = parent?.start.tz ?? event.start.tz ?? 'Etc/UTC'
  let allTimeZone = []
  const startTZ = moment.tz.zone(event.start.tz ?? 'Etc/UTC')
  allTimeZone.push(startTZ)

  if (event.end.tz && event.start.tz !== event.end.tz) {
    const endTZ = moment.tz.zone(event.end.tz)
    allTimeZone.push(endTZ)
  }

  let rrulStr = null
  if (event.rrule) {
    let rrule = event.rrule
    let untilStr = null
    // RRULE 객체에서 until 속성이 있는 경우. 변환시 Z를 붙이지 않아 오류 발생하므로 별도 처리
    const rruleOpts = convertToRRuleOptions(rrule.options as unknown as Record<string, unknown>)
    if (rruleOpts.until) {
      const backupUntil = rruleOpts.until as Date
      rruleOpts.until = null
      rrule = new RRule(rruleOpts) as unknown as typeof rrule
      untilStr = moment(backupUntil).utc().format('YYYYMMDDTHHmmss[Z]')
    }
    const rrulStrArr = rrule.toString().split('\n')
    rrulStr = rrulStrArr.find((str) => str.startsWith('RRULE:'))
    if (untilStr) {
      rrulStr += `;UNTIL=${untilStr}`
    }
  }

  let vEventStrings = 'BEGIN:VEVENT' + CRLF

  let summary = event.summary
  if (!summary) {
    if (parent?.summary) {
      summary = parent.summary
    } else {
      summary = ''
    }
  }
  summary = getStringFromVEventObject(summary)
  summary = encodeValue(summary)
  vEventStrings += 'SUMMARY:' + summary + CRLF

  let uid = event.uid
  if (!uid && parent?.uid) {
    uid = parent.uid
  }
  vEventStrings += 'UID:' + (uid ?? '') + CRLF

  let location = event.location
  if (!location && parent?.location) {
    location = parent.location
  }
  location = getStringFromVEventObject(location)
  location = encodeValue(location)
  vEventStrings += 'LOCATION:' + (location ?? '') + CRLF

  if (!parent) {
    let dtstamp = event.dtstamp
    if (!dtstamp) {
      let date = new Date()
      dtstamp = { ...date, tz: 'Etc/UTC' }
    }
    vEventStrings += 'DTSTAMP:' + moment(dtstamp).format('YYYYMMDDTHHmmss') + 'Z' + CRLF
  }

  if (event.datetype === 'date-time') {
    const startMoment = moment(event.start).tz(event.start.tz)
    const startTzId = startMoment.zoneName()
    if (startTzId === 'UTC' || startTzId === 'GMT') {
      vEventStrings += `DTSTART:${startMoment.format('YYYYMMDDTHHmmss[Z]')}${CRLF}`
    } else {
      vEventStrings += `DTSTART;TZID=${event.start.tz}:${startMoment.format('YYYYMMDDTHHmmss')}${CRLF}`
    }

    const endMoment = moment(event.end).tz(event.end.tz)
    const endTzId = endMoment.zoneName()
    if (endTzId === 'UTC' || endTzId === 'GMT') {
      vEventStrings += `DTEND:${endMoment.format('YYYYMMDDTHHmmss[Z]')}${CRLF}`
    } else {
      vEventStrings += `DTEND;TZID=${event.end.tz}:${endMoment.format('YYYYMMDDTHHmmss')}${CRLF}`
    }
  } else {
    // Allday
    vEventStrings +=
      'DTSTART;VALUE=DATE:' +
      moment(event.start, event.start.tz ?? 'Etc/UTC').format('YYYYMMDD') +
      CRLF
    vEventStrings +=
      'DTEND;VALUE=DATE:' + moment(event.end, event.end.tz ?? 'Etc/UTC').format('YYYYMMDD') + CRLF
  }

  let klass = event.class
  if (!klass && parent?.class) {
    klass = parent.class
  }
  if (!klass) {
    klass = 'PUBLIC'
  }
  vEventStrings += 'CLASS:' + klass + CRLF

  if (!event.sequence && String(event.sequence) !== '0') {
    ;(event as unknown as Record<string, unknown>).sequence = '0'
  }
  vEventStrings += 'SEQUENCE:' + event.sequence + CRLF

  let transp = event.transparency
  if (!transp && parent?.transparency) {
    transp = parent.transparency
  }
  if (!transp) {
    transp = 'TRANSPARENT'
  }
  vEventStrings += 'TRANSP:' + transp + CRLF

  //TODO: Category??? 일단 제외

  let description = event.description
  if (!description && parent?.description) {
    description = parent.description
  }
  description = getStringFromVEventObject(description)
  if (description && description.length > 0) {
    description = encodeValue(description)
    vEventStrings += 'DESCRIPTION:' + `${description}` + CRLF
  }

  let attendees = event.attendee
  // if (!attendees && parent?.attendee) {
  //   attendees = parent.attendee
  // } // parent 의 Attendee를 반영하지는 않는다.
  if (!Array.isArray(attendees)) {
    attendees = [attendees]
  }
  attendees &&
    attendees.forEach((attendee) => {
      if (!(typeof attendee === 'object' && 'val' in attendee)) {
        return
      }
      const attendeeValue = attendee.val
      let params = []
      let cn = attendee.params.CN
      if (!cn) {
        const head = attendee.val.split('@')[0]
        if (head.length > 0) {
          if (head.toLowerCase().startsWith('mailto:')) {
            cn = head.substring(7)
          } else {
            cn = head
          }
        }
      }
      if (cn) {
        params.push('CN=' + cn)
      }
      if (attendee.params.CUTYPE) {
        params.push('CUTYPE=' + attendee.params.CUTYPE)
      }
      if (attendee.params.ROLE) {
        if (attendee.params.ROLE !== 'ORGANIZER') {
          params.push('ROLE=' + attendee.params.ROLE)
        }
      }
      if (attendee.params.PARTSTAT) {
        params.push('PARTSTAT=' + attendee.params.PARTSTAT)
      }
      vEventStrings += 'ATTENDEE' + ';' + params.join(';') + ':' + attendeeValue + CRLF
    })

  if (attendees.length > 0) {
    let orgEvent = event
    if (parent) {
      orgEvent = parent
    }
    if (orgEvent.organizer) {
      if (typeof orgEvent.organizer === 'object') {
        const safeVal = ensureMailtoPrefix(orgEvent.organizer.val)
        if ('CN' in orgEvent.organizer.params) {
          vEventStrings += 'ORGANIZER;CN=' + orgEvent.organizer.params.CN + ':' + safeVal + CRLF
        } else {
          vEventStrings += 'ORGANIZER:' + safeVal + CRLF
        }
      } else if (typeof orgEvent.organizer === 'string') {
        vEventStrings += 'ORGANIZER:' + ensureMailtoPrefix(orgEvent.organizer) + CRLF
      }
    }
  }

  const author = event['NI-AUTHOR'] as { val: string; params: { CN: string } } | undefined
  if (author) {
    vEventStrings += 'X-NI-AUTHOR;CN=' + author.params.CN + ':' + author.val + CRLF
  }

  if (rrulStr) {
    vEventStrings += rrulStr + CRLF
  }

  if (event.recurrenceid) {
    const recurrenceIdMoment = moment(event.recurrenceid).tz(masterStartTZ)
    const recurrenceIdTzId = recurrenceIdMoment.zoneName()
    if (masterDateType === 'date-time') {
      if (recurrenceIdTzId === 'UTC' || recurrenceIdTzId === 'GMT') {
        vEventStrings += `RECURRENCE-ID:${recurrenceIdMoment.format('YYYYMMDDTHHmmss[Z]')}${CRLF}`
      } else {
        vEventStrings += `RECURRENCE-ID;TZID=${masterStartTZ}:${recurrenceIdMoment.format('YYYYMMDDTHHmmss')}${CRLF}`
      }
    } else {
      vEventStrings += `RECURRENCE-ID;VALUE=DATE:${moment(event.recurrenceid).format('YYYYMMDD')}${CRLF}`
    }
  }

  if (event['color']) {
    vEventStrings += 'COLOR:' + event['color'] + CRLF
  }

  if (appendString.length > 0) {
    vEventStrings += appendString
  }

  event.alarms &&
    event.alarms.forEach((alarm) => {
      vEventStrings += `BEGIN:VALARM${CRLF}TRIGGER:${alarm.trigger}${CRLF}ACTION:DISPLAY${CRLF}DESCRIPTION:REMINDER${CRLF}END:VALARM${CRLF}`
    })

  if (parent == null) {
    //
    const attachments = event['attach'] as
      | Array<{ val: string; params: Record<string, string> }>
      | undefined
    if (attachments && attachments.length > 0) {
      attachments.forEach((attachment) => {
        let attach = 'ATTACH'
        Object.keys(attachment.params).forEach((key) => {
          attach += `;${key}=${attachment.params[key]}`
        })
        attach += `:${attachment.val}${CRLF}`
        vEventStrings += attach
      })
    }
  }

  vEventStrings += 'END:VEVENT'

  return vEventStrings
}

function getStringFromVEventObject(obj: any): string {
  if (obj && typeof obj === 'object' && 'val' in obj) {
    return obj.val
  }
  return obj
}

/**
 * 지정된 타임존의 VTIMEZONE 컴포넌트를 생성합니다.
 */
export function generateVTimezone(tzName: string): string | null {
  const zone = moment.tz.zone(tzName)
  if (!zone) {
    console.warn(`Timezone not found: ${tzName}`)
    return null
  }

  const header = `BEGIN:VTIMEZONE${CRLF}TZID:${tzName}`
  const footer = 'END:VTIMEZONE'

  // DST가 더 이상 없는 타임존 처리 (예: Asia/Seoul)
  if (isNonDSTTimezone(zone)) {
    return generateNonDSTTimezone(zone, header, footer)
  }

  // DST가 있는 타임존 처리
  const startIndex = findTimezoneStartIndex(zone)
  if (startIndex === -1) {
    console.warn(`Could not find appropriate start index for timezone: ${tzName}`)
    return null
  }

  const tzContent = generateTimezoneContent(zone, startIndex)
  if (tzContent === CRLF) {
    return `${header}${CRLF}${footer}${CRLF}`
  }
  return `${header}${CRLF}${tzContent}${CRLF}${footer}${CRLF}`
}

/**
 * DST 변경이 없는 타임존인지 확인
 */
function isNonDSTTimezone(zone: moment.MomentZone): boolean {
  if (zone.untils.length === 1) {
    return true
  }
  return (
    zone.untils[zone.untils.length - 1] === Infinity &&
    zone.untils[zone.untils.length - 2] < moment().valueOf()
  )
}

/**
 * DST가 없는 타임존의 VTIMEZONE 컴포넌트 생성
 */
function generateNonDSTTimezone(zone: moment.MomentZone, header: string, footer: string): string {
  const startIndex = zone.untils.length - 1
  const currentTime = moment.tz(moment().valueOf(), zone.name)
  const beforeTime = moment.tz(zone.untils[startIndex - 1], zone.name)
  // const abbr = zone.abbrs[startIndex]

  const content = [
    'BEGIN:STANDARD',
    `DTSTART:${beforeTime.format('YYYYMMDDTHHmmss')}`,
    `TZOFFSETFROM:${beforeTime.format('ZZ')}`,
    `TZOFFSETTO:${currentTime.format('ZZ')}`,
    'END:STANDARD',
  ].join(CRLF)

  return `${header}${CRLF}${content}${CRLF}${footer}${CRLF}`
}

/**
 * 타임존 시작 인덱스 찾기
 */
function findTimezoneStartIndex(zone: moment.MomentZone): number {
  const currentYear = moment().year()
  let left = 0
  let right = zone.untils.length - 1

  while (left <= right) {
    const mid = Math.floor((left + right) / 2)
    if (zone.untils[mid] === Infinity) {
      right = mid - 1
      continue
    }

    const yearAtMid = moment(zone.untils[mid]).year()
    if (yearAtMid === currentYear) {
      return mid
    }
    if (yearAtMid < currentYear) {
      left = mid + 1
    } else {
      right = mid - 1
    }
  }

  return 1
}

/**
 * VTIMEZONE 컴포넌트의 내용 생성
 */
function generateTimezoneContent(zone: moment.MomentZone, startIndex: number): string {
  const items: string[] = []
  const maxOccurrences = 2

  for (
    let i = startIndex, count = 0;
    count < maxOccurrences && i + 1 < zone.untils.length;
    i++, count++
  ) {
    const current = moment.tz(zone.untils[i], zone.name)
    const next = moment.tz(
      zone.untils[i + 1] === Infinity ? moment() : zone.untils[i + 1],
      zone.name,
    )

    const isDST = current.isDST()
    const type = isDST ? 'DAYLIGHT' : 'STANDARD'

    let tzItems = []
    tzItems.push(`BEGIN:${type}`)
    tzItems.push(`DTSTART:${current.format('YYYYMMDDTHHmmss')}`)
    tzItems.push(`TZOFFSETFROM:${current.format('ZZ')}`)
    tzItems.push(
      `TZOFFSETTO:${zone.untils[i + 1] !== Infinity ? next.format('ZZ') : current.format('ZZ')}`,
    )
    tzItems.push(`END:${type}`)
    const item = tzItems.join(CRLF)

    // STANDARD는 앞에, DAYLIGHT는 뒤에 추가
    isDST ? items.push(item) : items.unshift(item)
  }

  return items.join(CRLF) + CRLF
}
